import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import { EarIcon } from "components/icons";
import ProductCategoryLabel from "components/product-category-label";
import {
  PATIENT_PRODUCT_STATUSES,
  PATIENT_PRODUCT_STATUS_COLORS
} from "constants/patient-products";
import useProductCategories from "hooks/use-product-categories.hook";
import type { Patient } from "services/types/patients.type";
import { formatBackendDateOnly } from "utils/common";
import { shouldShowPatientDeviceStatus } from "utils/patient-products.util";

import TextCaption from "./text-caption.component";

interface PatientHearingAidsProps {
  patient: Patient;
}

// This card never gated on `is_in_use`, so a device that is out for repair or still on its way
// to the patient has always shown here — with its delivery date, serial and warranty and
// nothing saying where it actually is. The header labels that state now
// (shouldShowPatientDeviceStatus), so label it here too rather than leaving the two surfaces
// disagreeing about the same device. Nothing else on the card moves.
function DeviceStatusChip({
  device
}: {
  device?: Patient["left_ear_hearing_aid"];
}) {
  const { palette } = useTheme();

  const status = device?.status;

  if (!shouldShowPatientDeviceStatus(status)) return null;

  const background = PATIENT_PRODUCT_STATUS_COLORS[status as number];

  // Returns its own Grid item so the row disappears entirely for a device that is with the
  // patient — an empty item would still take a line in the card.
  return (
    <Grid xs={12}>
      <Chip
        label={PATIENT_PRODUCT_STATUSES.getFromCode(status as number)}
        size="small"
        // sx, not the `color` prop — app/themes/components/chip.ts flattens every filled chip
        // to a single lilac background, so `color` renders every status identically.
        sx={{
          my: 0.5,
          bgcolor: background,
          color: palette.getContrastText(background)
        }}
      />
    </Grid>
  );
}

function DeviceCategory({
  device
}: {
  device?: Patient["left_ear_hearing_aid"];
}) {
  const { getCategory } = useProductCategories();
  const category = getCategory(device?.inventory_product?.product?.category);

  if (!category) return null;

  return (
    <Grid xs={12}>
      <ProductCategoryLabel category={category} variant="overline" />
    </Grid>
  );
}

export const PatientHearingAids: React.FC<PatientHearingAidsProps> = ({
  patient
}) => {
  const theme = useTheme();
  const patientHaveHearingAids = Boolean(
    patient?.left_ear_hearing_aid || patient?.right_ear_hearing_aid
  );

  return (
    <Stack gap={1} sx={{ background: misc.mutedPurple }}>
      <Stack gap={1} flexDirection="row">
        <Typography variant="body3" fontWeight="700" pb={0.5}>
          Hearing Aids
        </Typography>
        {!patientHaveHearingAids && (
          <Typography variant="body3">No Active Hearing Aids</Typography>
        )}
      </Stack>
      {patientHaveHearingAids && (
        <Stack gap={2} pb={0.5}>
          {patient?.left_ear_hearing_aid && (
            <Grid
              container
              xs={12}
              p={1.5}
              columnGap={1}
              sx={{
                border: `1px solid ${neutral[100]}`,
                borderRadius: "8px",
                lineHeight: 1.6,
                background: neutral[50],
                boxShadow: theme.shadows[3]
              }}
            >
              <Grid xs={3} display="flex" alignItems="center" pb={1}>
                <EarIcon />
                <Typography
                  variant="body2"
                  component="div"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  Left
                </Typography>
              </Grid>
              <Grid xs={8} textAlign="end">
                Delivery Date:{" "}
                {patient?.left_ear_hearing_aid?.delivered_at_display
                  ? formatBackendDateOnly(
                      patient?.left_ear_hearing_aid?.delivered_at_display,
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
              <DeviceCategory device={patient?.left_ear_hearing_aid} />
              <Grid xs={12}>{patient?.left_ear_hearing_aid?.display_name}</Grid>
              <DeviceStatusChip device={patient?.left_ear_hearing_aid} />
              <Grid xs={12}>
                <TextCaption>Serial Number:</TextCaption>{" "}
                {patient?.left_ear_hearing_aid?.serial_number || "-"}
              </Grid>
              <Grid xs={12}>
                <TextCaption>MFR Warranty Date:</TextCaption>{" "}
                {patient?.left_ear_hearing_aid
                  ?.manufacturer_warranty_expiration_date
                  ? formatBackendDateOnly(
                      String(
                        patient?.left_ear_hearing_aid
                          ?.manufacturer_warranty_expiration_date
                      ),
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
            </Grid>
          )}

          {patient?.right_ear_hearing_aid && (
            <Grid
              container
              xs={12}
              p={1.5}
              sx={{
                border: `1px solid ${neutral[100]}`,
                borderRadius: "8px",
                lineHeight: 1.6,
                background: neutral[50],
                boxShadow: theme.shadows[3]
              }}
            >
              <Grid xs={3} display="flex" alignItems="center" pb={1}>
                <EarIcon />
                <Typography
                  variant="body2"
                  component="div"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  Right
                </Typography>
              </Grid>
              <Grid xs={8} textAlign="end">
                Delivery Date:{" "}
                {patient?.right_ear_hearing_aid?.delivered_at_display
                  ? formatBackendDateOnly(
                      patient?.right_ear_hearing_aid?.delivered_at_display,
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
              <DeviceCategory device={patient?.right_ear_hearing_aid} />
              <Grid xs={12}>
                {patient?.right_ear_hearing_aid?.display_name}
              </Grid>
              <DeviceStatusChip device={patient?.right_ear_hearing_aid} />
              <Grid xs={12}>
                <TextCaption>Serial Number:</TextCaption>{" "}
                {patient?.right_ear_hearing_aid?.serial_number || "-"}
              </Grid>
              <Grid xs={12}>
                <TextCaption>MFR Warranty Date:</TextCaption>{" "}
                {patient?.right_ear_hearing_aid
                  ?.manufacturer_warranty_expiration_date
                  ? formatBackendDateOnly(
                      String(
                        patient?.right_ear_hearing_aid
                          ?.manufacturer_warranty_expiration_date
                      ),
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
            </Grid>
          )}
        </Stack>
      )}
    </Stack>
  );
};
